import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { type ReactNode, useEffect } from 'react';
import { log } from './lib/log';
import { PwaUpdater } from './components/pwa-updater';
import Login from './pages/Login';
import AppLayout from './layouts/AppLayout';
import Home from './pages/Home';
import UsersList from './pages/UsersList';
import UserCreate from './pages/UserCreate';
import UserEdit from './pages/UserEdit';
import AdminTenants from './pages/AdminTenants';
import ConversacionesIA from './pages/ConversacionesIA';
import Agente from './pages/Agente';
import Dashboard from './pages/Dashboard';
import Expedientes from './pages/Expedientes';
import ExpedienteNuevo from './pages/ExpedienteNuevo';
import ExpedienteDetail from './pages/ExpedienteDetail';
import PersonaDetail from './pages/PersonaDetail';
import RevisionList from './pages/RevisionList';
import RevisionDetail from './pages/RevisionDetail';
import Alertas from './pages/Alertas';
import AlertaDetalle from './pages/AlertaDetalle';
import Documentos from './pages/Documentos';
import MatrizRiesgo from './pages/MatrizRiesgo';
import AlcanceClientes from './pages/AlcanceClientes';
import Papelera from './pages/Papelera';
import Auditoria from './pages/Auditoria';
import Dossier from './pages/Dossier';
import NotFound from './pages/NotFound';
import { DemoProvider, DemoBar } from './lib/demo-tour';
import { FeatureDemoBanner } from './components/FeatureDemoBanner';
import { AssistantChatProvider } from './components/assistant';
import { useMe } from './lib/hooks';
import { DEV_AUTH } from './lib/api';
import { useTranslation } from 'react-i18next';

/** Role-gated route (UX only — the server enforces RBAC, §9). */
function RequireRole({ roles, children }: { roles: string[]; children: ReactNode }) {
  const { t } = useTranslation();
  const { data: me, isLoading } = useMe();
  if (isLoading) return <p className="text-ink-soft">{t('common.loading')}</p>;
  if (!me || !roles.some((r) => me.roles.includes(r))) {
    return <Navigate to="/usuarios" replace />;
  }
  return <>{children}</>;
}

// Chromeless auth guard for the focused, full-screen routes (the intake wizard,
// the print dossier) that live OUTSIDE the AppLayout shell — the shell provides
// this guard for every other route. Renders nothing while loading, then bounces
// an unauthenticated visitor to /login (mirrors AppLayout's guard).
function RequireAuth({ children }: { children: ReactNode }) {
  const { data: me, isError, isLoading } = useMe();
  if (isLoading) return null;
  if (isError || !me) return <Navigate to="/login" replace state={{ sessionExpired: true }} />;
  return <>{children}</>;
}

/** Logs each client-side navigation so a session reads as a trail in the console. */
function RouteLogger() {
  const location = useLocation();
  useEffect(() => {
    log.child('route').info('navigate', { to: location.pathname });
  }, [location.pathname]);
  return null;
}

/**
 * Reset the window scroll to the top on every genuine route change, so navigating
 * between screens never lands you mid-page at the previous route's scroll offset.
 * Keyed on `pathname` ONLY — query-param/hash updates don't jump the page to top —
 * and instant (not smooth) so the destination renders already at the top.
 *
 * Two deliberate carve-outs:
 * - `/agente` manages its OWN scroll (it auto-scrolls to the newest message), so
 *   we no-op there and let the thread's stick-to-bottom win.
 * - It never fights `useScrollMemory` (lib/use-scroll-memory.ts): that restores
 *   scroll for in-page tab toggles on the SAME route (no pathname change), so it
 *   only runs when this component stays dormant. On a real route change the
 *   destination has nothing stored yet, so resetting to top is the correct start.
 */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname.startsWith('/agente')) return;
    window.scrollTo({ top: 0 });
  }, [pathname]);
  return null;
}

export default function App() {
  return (
    <DemoProvider>
      <AssistantChatProvider>
      <FeatureDemoBanner />
      <RouteLogger />
      <ScrollToTop />
      <Routes>
      {/* Local dev-auth demo has no login portal: bounce /login into the app.
          Production (real Cognito + MFA) still renders the real Login. */}
      <Route path="/login" element={DEV_AUTH ? <Navigate to="/agente" replace /> : <Login />} />

      {/* Focused, chromeless routes — no sidebar shell. The guided intake wizard
          stays full-screen; the dossier is a print view. */}
      <Route path="/expedientes/nuevo" element={<RequireAuth><ExpedienteNuevo /></RequireAuth>} />
      <Route path="/expedientes/:id/dossier" element={<RequireAuth><Dossier /></RequireAuth>} />

      {/* One app shell — shared top bar + collapsible sidebar (AppLayout). The
          former Registro screens now live here too, alongside Cumplimiento.
          The Agente is the app's default screen: bare "/" resolves to it, and
          Registro's Home lives at /inicio, reached via its sidebar tab. */}
      <Route element={<AppLayout />}>
        <Route index element={<Navigate to="/agente" replace />} />
        <Route path="inicio" element={<Home />} />
        <Route path="expedientes" element={<Expedientes />} />
        <Route path="expedientes/:id" element={<ExpedienteDetail />} />
        {/* Fase 4 drill-down (req#7): una persona relacionada + sus documentos exigidos. */}
        <Route path="expedientes/:id/personas/:ownerId" element={<PersonaDetail />} />
        <Route path="agente" element={<Agente />} />
        <Route path="panel" element={<Dashboard />} />
        <Route path="revision" element={<RevisionList />} />
        <Route path="revision/:id" element={<RevisionDetail />} />
        <Route path="alertas" element={<Alertas />} />
        <Route path="alertas/:id" element={<AlertaDetalle />} />
        <Route path="documentos" element={<Documentos />} />
        <Route path="alcance" element={<AlcanceClientes />} />
        {/* Validación masiva y Vigilancia Registral ahora son pestañas de Alertas. */}
        <Route path="validacion-masiva" element={<Navigate to="/alertas?tab=validacion-masiva" replace />} />
        <Route path="matriz-riesgo" element={<MatrizRiesgo />} />
        <Route path="vigilancia-registral" element={<Navigate to="/alertas?tab=vigilancia-registral" replace />} />
        <Route
          path="papelera"
          element={
            <RequireRole roles={['tenant_admin', 'compliance_manager', 'intake_officer', 'analyst', 'auditor']}>
              <Papelera />
            </RequireRole>
          }
        />
        <Route
          path="auditoria"
          element={
            <RequireRole roles={['tenant_admin', 'compliance_manager', 'auditor']}>
              <Auditoria />
            </RequireRole>
          }
        />
        <Route path="usuarios" element={<UsersList />} />
        <Route path="usuarios/nuevo" element={<UserCreate />} />
        <Route path="usuarios/:id" element={<UserEdit />} />
        <Route
          path="admin/entidades"
          element={
            <RequireRole roles={['super_admin']}>
              <AdminTenants />
            </RequireRole>
          }
        />
        <Route
          path="admin/conversaciones-ia"
          element={
            <RequireRole roles={['super_admin']}>
              <ConversacionesIA />
            </RequireRole>
          }
        />
      </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
      <DemoBar />
      <PwaUpdater />
      </AssistantChatProvider>
    </DemoProvider>
  );
}
